import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { node, object } from 'prop-types';
import { Alert } from '@ajaibid/components';

const INITIAL_ALERT_STATE = {
  text: '',
  action: null,
  type: 'error',
  show: false,
  shownDuration: 0,
  dataTestId: 'divAlert',
};

/* eslint-disable no-unused-vars */
const INITIAL_CONTEXT_VALUE = {
  showAlert: (
    data = '',
    type = '',
    action = { text: '', onClick: () => {} },
    shownDuration = 0,
    dataTestId = ''
  ) => {},
  hideAlert: () => {},
};

export const AlertContext = React.createContext(INITIAL_CONTEXT_VALUE);

const AlertProvider = ({ children }) => {
  const [state, setState] = useState(INITIAL_ALERT_STATE);

  const showAlert = useCallback(
    /**
     * Function to show a Alert.
     * @param {string} text Text inside the Alert body
     * @param {string} type alert type of the Alert
     * @param {{text: string, onClick: Function}} action
     * CTA text of the Alert and Callback when CTA text is clicked
     * @param {number} shownDuration How many milliseconds before auto-hiding Alert. default: 3000ms
     * @param {string} dataTestId custom data-testid
     */
    (data = '', type = 'error', action = null, shownDuration = 3000, dataTestId = 'divAlert') => {
      if (typeof data === 'object') {
        setState({
          text: data.text,
          action: data.action,
          type: data.type,
          show: true,
          shownDuration: data.shownDuration || shownDuration,
          dataTestId: data.dataTestId,
        });
      } else {
        setState({
          text: data,
          action,
          type,
          show: true,
          shownDuration,
          dataTestId,
        });
      }
    },
    []
  );

  /**
   * Function to hide the Alert.
   */
  const hideAlert = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      show: false,
      shownDuration: 0,
    }));
  }, []);

  useEffect(() => {
    let timeout;

    if (state.shownDuration) {
      timeout = setTimeout(hideAlert, state.shownDuration);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [state.shownDuration, hideAlert]);

  const alertProps = {
    text: state.text,
    action: state.action,
    type: state.type,
    show: state.show,
    dataTestId: state.dataTestId,
  };

  const contextProps = useMemo(
    () => ({
      showAlert,
      hideAlert,
    }),
    [showAlert, hideAlert]
  );

  return (
    <AlertContext.Provider value={contextProps}>
      <Alert {...alertProps} />
      {children}
    </AlertContext.Provider>
  );
};

AlertProvider.propTypes = {
  children: node.isRequired,
  defaultState: object,
};
AlertProvider.defaultProps = {
  defaultState: {},
};

export default AlertProvider;
